export const MA_TARGET_PACK_SCHEMA_VERSION = 'ma_target_pack_v1' as const;

/**
 * The safe-token grammar every target pack id must satisfy: lowercase, digits, internal
 * hyphens only. No `.`, `/`, `\`, or leading hyphen — a value this pattern accepts cannot
 * contain a path separator or a `..` traversal segment, so a caller that validates against
 * it BEFORE touching the filesystem (see `resolveTargetPackPath` in
 * `ma-synergy-tracking.target-pack-resolution.ts`) never has to sanitize a path afterward.
 */
export const MA_TARGET_PACK_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,119}$/;
export const MA_FUSION_TWO_ORG_DEAL_CANVAS_SCHEMA_VERSION =
  'ma_fusion_two_org_deal_canvas_v1' as const;
export const MA_FUSION_TWO_ORG_DEAL_CANVAS_PROJECTION_SCHEMA_VERSION =
  'ma_fusion_two_org_deal_canvas_projection_v1' as const;

export const MA_TARGET_PACK_CERTAINTY_CLASSES = [
  'public_filing',
  'ir_material',
  'extrapolated_assumption',
] as const;

export type MaTargetPackSchemaVersion = typeof MA_TARGET_PACK_SCHEMA_VERSION;
export type MaTargetPackCertaintyClass = (typeof MA_TARGET_PACK_CERTAINTY_CLASSES)[number];

export type MaTargetClass =
  | 'freight_forwarder'
  | 'managed_transportation_4pl'
  | 'regional_brokerage_consolidation';

export type MaTargetPackField<T> = Readonly<{
  value: T;
  certaintyClass: MaTargetPackCertaintyClass;
  sourceRefs: readonly string[];
  notes?: string;
}>;

export type MaTargetPackSource = Readonly<{
  sourceId: string;
  title: string;
  url: string;
  sourceType:
    | 'sec_filing'
    | 'sec_companyfacts'
    | 'company_ir'
    | 'company_leadership'
    | 'github_issue'
    | 'helix_policy';
  retrievedAt: string;
}>;

export const MA_TARGET_SCENARIO_ROSTER_SCHEMA_VERSION =
  'ma_target_scenario_roster_v1' as const;

export type MaTargetPackScenarioOffice = Readonly<{
  id: string;
  name: string;
  country: string;
}>;

export type MaTargetPackScenarioRosterFunction = Readonly<{
  functionName: string;
  headcount: number;
  executiveTitle: string;
  roleTitles: readonly string[];
}>;

export type MaTargetPackScenarioRoster = Readonly<{
  schemaVersion: typeof MA_TARGET_SCENARIO_ROSTER_SCHEMA_VERSION;
  organizationName: string;
  totalHeadcount: number;
  rootLeader: Readonly<{
    name: string;
    title: string;
  }>;
  offices: readonly MaTargetPackScenarioOffice[];
  functions: readonly MaTargetPackScenarioRosterFunction[];
}>;

export type MaTargetPack = Readonly<{
  schemaVersion: MaTargetPackSchemaVersion;
  packId: string;
  targetClass: MaTargetClass;
  identity: Readonly<{
    legalName: MaTargetPackField<string>;
    ticker: MaTargetPackField<string | null>;
    exchange: MaTargetPackField<string | null>;
    industry: MaTargetPackField<string>;
    headquarters: MaTargetPackField<string>;
    acquisitionResonance: MaTargetPackField<string>;
  }>;
  financials: Readonly<{
    fiscalPeriod: MaTargetPackField<string>;
    revenueUsd: MaTargetPackField<number>;
    operatingIncomeUsd: MaTargetPackField<number | null>;
    sgaUsd: MaTargetPackField<number | null>;
  }>;
  workforce: Readonly<{
    totalHeadcount: MaTargetPackField<number | null>;
    byFunction: readonly Readonly<{
      functionName: MaTargetPackField<string>;
      headcount: MaTargetPackField<{ low: number; high: number }>;
    }>[];
    byGeography: readonly Readonly<{
      geography: MaTargetPackField<string>;
      headcount: MaTargetPackField<{ low: number; high: number }>;
    }>[];
    compCostAggregates: Readonly<{
      annualCompCostEnvelopeUsd: MaTargetPackField<{ low: number; high: number }>;
      benefitsLoadPct: MaTargetPackField<{ low: number; high: number }>;
    }>;
  }>;
  leadership: Readonly<{
    topTwoLayers: readonly Readonly<{
      name: MaTargetPackField<string>;
      title: MaTargetPackField<string>;
      layer: MaTargetPackField<'layer_1' | 'layer_2'>;
      bio: MaTargetPackField<string>;
    }>[];
  }>;
  strategy: Readonly<{
    statedStrategy: MaTargetPackField<string>;
    integrationHooks: readonly MaTargetPackField<string>[];
  }>;
  dealThesis: Readonly<{
    whyChrwWouldBuy: MaTargetPackField<string>;
    integrateAndCutCostThesis: MaTargetPackField<string>;
    expectedOverlapZones: readonly MaTargetPackField<string>[];
  }>;
  scenarioRoster?: MaTargetPackScenarioRoster;
  dataGaps: readonly string[];
  sources: readonly MaTargetPackSource[];
}>;

import type { OrgPerson, OrgPosition, OrgState } from './org-state';

export type MaFusionEvidenceRef = Readonly<{
  certaintyClass: MaTargetPackCertaintyClass;
  sourceRefs: readonly string[];
  notes?: string;
}>;

export type MaFusionModeledTargetPosition = OrgPosition &
  Readonly<{
    functionName?: string;
    level?: string;
    location?: string;
    modeledHeadcountRange?: { low: number; high: number };
    maEvidence?: Record<string, MaFusionEvidenceRef>;
  }>;

export type MaFusionModeledTargetOrgState = Omit<OrgState, 'positions' | 'people'> &
  Readonly<{
    positions: MaFusionModeledTargetPosition[];
    people: OrgPerson[];
  }>;

export type MaFusionModeledTarget = Readonly<{
  schemaVersion: 'ma_fusion_modeled_target_v1';
  packId: string;
  targetClass: MaTargetClass;
  orgState: MaFusionModeledTargetOrgState;
  dealThesis: MaTargetPackField<string>;
  evidenceByPath: Record<string, MaFusionEvidenceRef>;
  certaintySummary: Record<MaTargetPackCertaintyClass, number>;
  financials: MaTargetPack['financials'];
  workforce: MaTargetPack['workforce'];
  strategy: MaTargetPack['strategy'];
  targetDealThesis: MaTargetPack['dealThesis'];
  dataGaps: readonly string[];
  sources: readonly MaTargetPackSource[];
}>;

export type MaFusionDryRunOrgCostRow = Readonly<{
  positionId: string;
  fullyLoadedAnnualCost?: number | null;
  currency?: string | null;
  costCenter?: string | null;
}>;

export type MaFusionDealCanvasOrgSide = 'PRIMARY' | 'TARGET';

export type MaFusionDealCanvasOrganization = Readonly<{
  orgSide: MaFusionDealCanvasOrgSide;
  orgStateId: string;
  name: string;
  positions: readonly MaFusionModeledTargetPosition[];
  people: readonly OrgPerson[];
}>;

export type MaFusionDealCanvasMemberRef = Readonly<{
  orgSide: MaFusionDealCanvasOrgSide;
  positionId: string;
}>;

export type MaFusionDealCanvasSharedMemberFamily = Readonly<{
  familyId: string;
  label: string;
  certaintyClass: MaTargetPackCertaintyClass;
  matchMethod: 'exact_function_title' | 'modeled_overlap_zone';
  members: readonly MaFusionDealCanvasMemberRef[];
}>;

export type MaFusionTwoOrgDealCanvas = Readonly<{
  schemaVersion: typeof MA_FUSION_TWO_ORG_DEAL_CANVAS_SCHEMA_VERSION;
  organizations: Readonly<{
    acquirer: MaFusionDealCanvasOrganization;
    target: MaFusionDealCanvasOrganization;
  }>;
  sharedMemberFamilies: readonly MaFusionDealCanvasSharedMemberFamily[];
}>;

export type MaFusionDealCanvasNode = Readonly<{
  id: string;
  type: 'orgNodeCard';
  position: Readonly<{ x: number; y: number }>;
  data: Readonly<{
    label: string;
    title: string;
    positionId: string;
    orgStateId: string;
    occupantName: string | null;
    occupantId: string | null;
    isRoot: boolean;
    attrs: Readonly<{
      orgSide: MaFusionDealCanvasOrgSide;
      organizationName: string;
      reportsTo: string | null;
      functionName: string | null;
      level: string | null;
      location: string | null;
      modeledHeadcountRange: Readonly<{ low: number; high: number }> | null;
      certaintyClass: MaTargetPackCertaintyClass | null;
    }>;
  }>;
}>;

export type MaFusionDealCanvasEdge = Readonly<{
  id: string;
  source: string;
  target: string;
  type: 'smoothstep';
  label?: string;
  animated?: boolean;
  style?: Readonly<{ strokeDasharray: string; strokeWidth: number }>;
  data: Readonly<
    | {
        kind: 'MANAGES';
        orgSide: MaFusionDealCanvasOrgSide;
      }
    | {
        kind: 'OVERLAP';
        familyId: string;
        certaintyClass: MaTargetPackCertaintyClass;
        matchMethod: MaFusionDealCanvasSharedMemberFamily['matchMethod'];
        memberPositionIds: readonly string[];
      }
  >;
}>;

export type MaFusionTwoOrgDealCanvasProjection = Readonly<{
  schemaVersion: typeof MA_FUSION_TWO_ORG_DEAL_CANVAS_PROJECTION_SCHEMA_VERSION;
  nodes: MaFusionDealCanvasNode[];
  edges: MaFusionDealCanvasEdge[];
}>;

export type MaFusionDemoDryRun = Readonly<{
  mode: 'dry_run_no_db_writes';
  scenario: Readonly<{
    name: string;
    primaryOrgStateId: string;
    targetOrgStateId: string;
    dealThesis: string;
    assumptionsJson: Record<string, unknown>;
  }>;
  acquirer: Readonly<{
    orgState: MaFusionModeledTargetOrgState;
    costRows: readonly MaFusionDryRunOrgCostRow[];
  }>;
  target: MaFusionModeledTarget;
  combinedSnapshot: Readonly<{
    name: string;
    positions: readonly MaFusionModeledTargetPosition[];
    people: readonly OrgPerson[];
  }>;
  overlapSequence: Readonly<{
    workforceGrid: {
      totalRows: number;
      functions: readonly string[];
      locations: readonly string[];
      rows: readonly Record<string, unknown>[];
    };
    crosswalk: { rows: readonly Record<string, unknown>[] };
    leadershipSlate: { rows: readonly Record<string, unknown>[] };
    redundancyCandidates: { rows: readonly Record<string, unknown>[] };
    sharedMemberFamilies: readonly MaFusionDealCanvasSharedMemberFamily[];
  }>;
  twoOrgDealCanvas: Readonly<{
    model: MaFusionTwoOrgDealCanvas;
    projection: MaFusionTwoOrgDealCanvasProjection;
  }>;
  m3GapList: readonly string[];
}>;

type MaTargetPackRecord = Record<string, unknown>;

const MA_TARGET_CLASSES = new Set<MaTargetClass>([
  'freight_forwarder',
  'managed_transportation_4pl',
  'regional_brokerage_consolidation',
]);

function isRecord(value: unknown): value is MaTargetPackRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasOwn(value: MaTargetPackRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function validatePackField(
  value: unknown,
  path: string,
  sourceIds: ReadonlySet<string>,
  errors: string[]
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  if (!hasOwn(value, 'value')) {
    errors.push(`${path}.value is required`);
  }
  if (!hasOwn(value, 'certaintyClass')) {
    errors.push(`${path}.certaintyClass is required`);
  } else if (
    !MA_TARGET_PACK_CERTAINTY_CLASSES.includes(value.certaintyClass as MaTargetPackCertaintyClass)
  ) {
    errors.push(
      `${path}.certaintyClass must be one of ${MA_TARGET_PACK_CERTAINTY_CLASSES.join(', ')}`
    );
  }
  const sourceRefs = Array.isArray(value.sourceRefs) ? value.sourceRefs : [];
  if (!sourceRefs.length) {
    errors.push(`${path}.sourceRefs must be non-empty`);
  }
  for (const sourceRef of sourceRefs) {
    if (typeof sourceRef !== 'string' || !sourceIds.has(sourceRef)) {
      errors.push(`${path}.sourceRefs includes unknown source "${String(sourceRef)}"`);
    }
  }
  if (value.certaintyClass === 'extrapolated_assumption' && !String(value.notes ?? '').trim()) {
    errors.push(`${path} extrapolated_assumption fields must carry notes`);
  }
}

function walkPackFields(
  value: unknown,
  path: string,
  sourceIds: ReadonlySet<string>,
  errors: string[]
): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) => walkPackFields(item, `${path}[${index}]`, sourceIds, errors));
    return;
  }
  if (!isRecord(value)) return;
  if (hasOwn(value, 'value')) {
    validatePackField(value, path, sourceIds, errors);
    return;
  }
  for (const [key, nested] of Object.entries(value)) {
    walkPackFields(nested, path ? `${path}.${key}` : key, sourceIds, errors);
  }
}

function requireRecord(value: unknown, path: string, errors: string[]): MaTargetPackRecord | null {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return null;
  }
  return value;
}

function requireArray(value: unknown, path: string, errors: string[]): unknown[] {
  if (!Array.isArray(value) || !value.length) {
    errors.push(`${path} must be a non-empty array`);
    return [];
  }
  return value;
}

function requireScenarioText(
  parent: MaTargetPackRecord | null,
  key: string,
  path: string,
  errors: string[],
): string | null {
  const value = String(parent?.[key] ?? '').trim();
  if (!value) {
    errors.push(`${path}.${key} is required`);
    return null;
  }
  return value;
}

function validateScenarioRoster(
  value: unknown,
  expectedHeadcount: unknown,
  errors: string[],
): void {
  const roster = requireRecord(value, 'scenarioRoster', errors);
  if (!roster) return;

  if (roster.schemaVersion !== MA_TARGET_SCENARIO_ROSTER_SCHEMA_VERSION) {
    errors.push(`scenarioRoster.schemaVersion must be ${MA_TARGET_SCENARIO_ROSTER_SCHEMA_VERSION}`);
  }
  requireScenarioText(roster, 'organizationName', 'scenarioRoster', errors);
  const totalHeadcount = roster.totalHeadcount;
  if (!Number.isInteger(totalHeadcount) || Number(totalHeadcount) <= 0) {
    errors.push('scenarioRoster.totalHeadcount must be a positive integer');
  } else if (totalHeadcount !== expectedHeadcount) {
    errors.push('scenarioRoster.totalHeadcount must match workforce.totalHeadcount.value');
  }

  const rootLeader = requireRecord(roster.rootLeader, 'scenarioRoster.rootLeader', errors);
  requireScenarioText(rootLeader, 'name', 'scenarioRoster.rootLeader', errors);
  requireScenarioText(rootLeader, 'title', 'scenarioRoster.rootLeader', errors);

  const offices = requireArray(roster.offices, 'scenarioRoster.offices', errors);
  const officeIds = new Set<string>();
  for (const [index, value] of offices.entries()) {
    const office = requireRecord(value, `scenarioRoster.offices[${index}]`, errors);
    const id = requireScenarioText(office, 'id', `scenarioRoster.offices[${index}]`, errors);
    requireScenarioText(office, 'name', `scenarioRoster.offices[${index}]`, errors);
    requireScenarioText(office, 'country', `scenarioRoster.offices[${index}]`, errors);
    if (id && officeIds.has(id)) {
      errors.push(`scenarioRoster.offices[${index}].id is duplicated: ${id}`);
    }
    if (id) officeIds.add(id);
  }

  const functions = requireArray(roster.functions, 'scenarioRoster.functions', errors);
  const functionNames = new Set<string>();
  let functionHeadcount = 0;
  for (const [index, value] of functions.entries()) {
    const rosterFunction = requireRecord(value, `scenarioRoster.functions[${index}]`, errors);
    const functionName = requireScenarioText(
      rosterFunction,
      'functionName',
      `scenarioRoster.functions[${index}]`,
      errors,
    );
    requireScenarioText(rosterFunction, 'executiveTitle', `scenarioRoster.functions[${index}]`, errors);
    const headcount = rosterFunction?.headcount;
    if (!Number.isInteger(headcount) || Number(headcount) <= 0) {
      errors.push(`scenarioRoster.functions[${index}].headcount must be a positive integer`);
    } else {
      functionHeadcount += Number(headcount);
    }
    const roleTitles = Array.isArray(rosterFunction?.roleTitles)
      ? rosterFunction.roleTitles.map((title) => String(title).trim()).filter(Boolean)
      : [];
    if (!roleTitles.length) {
      errors.push(`scenarioRoster.functions[${index}].roleTitles must be a non-empty array`);
    }
    if (functionName && functionNames.has(functionName)) {
      errors.push(`scenarioRoster.functions[${index}].functionName is duplicated: ${functionName}`);
    }
    if (functionName) functionNames.add(functionName);
  }
  if (Number.isInteger(totalHeadcount) && functionHeadcount !== totalHeadcount) {
    errors.push('scenarioRoster.functions headcount must sum to scenarioRoster.totalHeadcount');
  }
}

function requireField(
  parent: MaTargetPackRecord | null,
  key: string,
  path: string,
  sourceIds: ReadonlySet<string>,
  errors: string[]
): void {
  if (!parent || !hasOwn(parent, key)) {
    errors.push(`${path} is required`);
    return;
  }
  validatePackField(parent[key], path, sourceIds, errors);
}

/** Runtime parser for uploaded or API-supplied pre-deal target packs. */
export function parseMaTargetPack(input: unknown): MaTargetPack {
  const errors: string[] = [];
  const pack = requireRecord(input, 'pack', errors);
  if (!pack) {
    throw new Error(`Invalid M&A target pack:\n- ${errors.join('\n- ')}`);
  }

  if (pack.schemaVersion !== MA_TARGET_PACK_SCHEMA_VERSION) {
    errors.push(`schemaVersion must be ${MA_TARGET_PACK_SCHEMA_VERSION}`);
  }
  const packId = String(pack.packId ?? '').trim();
  if (!MA_TARGET_PACK_ID_PATTERN.test(packId)) {
    errors.push('packId must be a lowercase safe token');
  }
  if (!MA_TARGET_CLASSES.has(pack.targetClass as MaTargetClass)) {
    errors.push(`targetClass must be one of ${Array.from(MA_TARGET_CLASSES).join(', ')}`);
  }

  const sources = requireArray(pack.sources, 'sources', errors);
  const sourceIds = new Set<string>();
  sources.forEach((source, index) => {
    const sourceRecord = requireRecord(source, `sources[${index}]`, errors);
    if (!sourceRecord) return;
    const sourceId = String(sourceRecord.sourceId ?? '').trim();
    if (!/^[A-Za-z0-9._-]{1,120}$/.test(sourceId)) {
      errors.push(`sources[${index}].sourceId must be a safe token`);
    } else if (sourceIds.has(sourceId)) {
      errors.push(`duplicate sourceId ${sourceId}`);
    } else {
      sourceIds.add(sourceId);
    }
    for (const key of ['title', 'sourceType', 'retrievedAt'] as const) {
      if (!String(sourceRecord[key] ?? '').trim()) {
        errors.push(`sources[${index}].${key} is required`);
      }
    }
    const sourceUrl = String(sourceRecord.url ?? '').trim();
    const isRepoDoc = sourceUrl.startsWith('docs/') && !sourceUrl.includes('..');
    let isHttps = false;
    try {
      isHttps = new URL(sourceUrl).protocol === 'https:';
    } catch {
      isHttps = false;
    }
    if (!isRepoDoc && !isHttps) {
      errors.push(`sources[${index}].url must be https or a repo-local docs path`);
    }
  });

  const identity = requireRecord(pack.identity, 'identity', errors);
  for (const key of [
    'legalName',
    'ticker',
    'exchange',
    'industry',
    'headquarters',
    'acquisitionResonance',
  ]) {
    requireField(identity, key, `identity.${key}`, sourceIds, errors);
  }

  const financials = requireRecord(pack.financials, 'financials', errors);
  for (const key of ['fiscalPeriod', 'revenueUsd', 'operatingIncomeUsd', 'sgaUsd']) {
    requireField(financials, key, `financials.${key}`, sourceIds, errors);
  }

  const workforce = requireRecord(pack.workforce, 'workforce', errors);
  requireField(workforce, 'totalHeadcount', 'workforce.totalHeadcount', sourceIds, errors);
  const byFunction = requireArray(workforce?.byFunction, 'workforce.byFunction', errors);
  byFunction.forEach((row, index) => {
    const record = requireRecord(row, `workforce.byFunction[${index}]`, errors);
    requireField(
      record,
      'functionName',
      `workforce.byFunction[${index}].functionName`,
      sourceIds,
      errors
    );
    requireField(
      record,
      'headcount',
      `workforce.byFunction[${index}].headcount`,
      sourceIds,
      errors
    );
  });
  const byGeography = requireArray(workforce?.byGeography, 'workforce.byGeography', errors);
  byGeography.forEach((row, index) => {
    const record = requireRecord(row, `workforce.byGeography[${index}]`, errors);
    requireField(
      record,
      'geography',
      `workforce.byGeography[${index}].geography`,
      sourceIds,
      errors
    );
    requireField(
      record,
      'headcount',
      `workforce.byGeography[${index}].headcount`,
      sourceIds,
      errors
    );
  });
  const comp = requireRecord(workforce?.compCostAggregates, 'workforce.compCostAggregates', errors);
  requireField(
    comp,
    'annualCompCostEnvelopeUsd',
    'workforce.compCostAggregates.annualCompCostEnvelopeUsd',
    sourceIds,
    errors
  );
  requireField(
    comp,
    'benefitsLoadPct',
    'workforce.compCostAggregates.benefitsLoadPct',
    sourceIds,
    errors
  );

  const leadership = requireRecord(pack.leadership, 'leadership', errors);
  const leaders = requireArray(leadership?.topTwoLayers, 'leadership.topTwoLayers', errors);
  leaders.forEach((row, index) => {
    const record = requireRecord(row, `leadership.topTwoLayers[${index}]`, errors);
    for (const key of ['name', 'title', 'layer', 'bio']) {
      requireField(record, key, `leadership.topTwoLayers[${index}].${key}`, sourceIds, errors);
    }
  });

  const strategy = requireRecord(pack.strategy, 'strategy', errors);
  requireField(strategy, 'statedStrategy', 'strategy.statedStrategy', sourceIds, errors);
  requireArray(strategy?.integrationHooks, 'strategy.integrationHooks', errors);

  const dealThesis = requireRecord(pack.dealThesis, 'dealThesis', errors);
  requireField(dealThesis, 'whyChrwWouldBuy', 'dealThesis.whyChrwWouldBuy', sourceIds, errors);
  requireField(
    dealThesis,
    'integrateAndCutCostThesis',
    'dealThesis.integrateAndCutCostThesis',
    sourceIds,
    errors
  );
  requireArray(dealThesis?.expectedOverlapZones, 'dealThesis.expectedOverlapZones', errors);

  for (const section of [
    'identity',
    'financials',
    'workforce',
    'leadership',
    'strategy',
    'dealThesis',
  ]) {
    walkPackFields(pack[section], section, sourceIds, errors);
  }
  if (!Array.isArray(pack.dataGaps) || !pack.dataGaps.length) {
    errors.push('dataGaps must name unavailable or modeled diligence fields');
  }
  if (pack.scenarioRoster !== undefined) {
    const workforceTotalHeadcount = isRecord(workforce?.totalHeadcount)
      ? workforce.totalHeadcount.value
      : undefined;
    validateScenarioRoster(pack.scenarioRoster, workforceTotalHeadcount, errors);
  }

  if (errors.length) {
    throw new Error(`Invalid M&A target pack:\n- ${Array.from(new Set(errors)).join('\n- ')}`);
  }
  return input as MaTargetPack;
}

type BuildModeledTargetOptions = Readonly<{
  tenantId: string;
  dealThesis: string;
}>;

type BuildDemoDryRunInput = Readonly<{
  targetPack: MaTargetPack;
  dealThesis: string;
  acquirer: Readonly<{
    tenantId: string;
    orgStateId: string;
    name: string;
    orgStructureCsv: string;
    orgCostRows: readonly MaFusionDryRunOrgCostRow[];
  }>;
}>;

const evidenceRef = (field: MaTargetPackField<unknown>): MaFusionEvidenceRef => ({
  certaintyClass: field.certaintyClass,
  sourceRefs: field.sourceRefs,
  ...(field.notes ? { notes: field.notes } : {}),
});

const addEvidence = (
  evidenceByPath: Record<string, MaFusionEvidenceRef>,
  path: string,
  field: MaTargetPackField<unknown>
) => {
  evidenceByPath[path] = evidenceRef(field);
};

const blankCertaintySummary = (): Record<MaTargetPackCertaintyClass, number> => ({
  public_filing: 0,
  ir_material: 0,
  extrapolated_assumption: 0,
});

function summarizeCertainty(evidenceByPath: Record<string, MaFusionEvidenceRef>) {
  const summary = blankCertaintySummary();
  for (const evidence of Object.values(evidenceByPath)) {
    summary[evidence.certaintyClass] += 1;
  }
  return summary;
}

function inferFunctionFromTitle(title: string): string {
  const normalized = title.toLowerCase();
  if (normalized.includes('financial') || normalized.includes('finance')) return 'Corporate SG&A';
  if (normalized.includes('brokerage')) return 'Brokerage operations';
  if (normalized.includes('forwarding')) return 'Global forwarding operations';
  if (normalized.includes('executive') || normalized.includes('chief'))
    return 'Executive leadership';
  return 'Leadership';
}

function normalizeTitleKey(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeFunctionName(value: unknown): string {
  return String(value ?? '').trim();
}

function targetToAcquirerOverlapMatcher(targetPosition: MaFusionModeledTargetPosition) {
  const text = `${targetPosition.title} ${targetPosition.functionName ?? ''}`.toLowerCase();
  if (text.includes('forwarding') || text.includes('customs')) {
    return (candidate: MaFusionModeledTargetPosition) =>
      `${candidate.title} ${candidate.functionName ?? ''}`
        .toLowerCase()
        .includes('global forwarding');
  }
  if (text.includes('brokerage') || text.includes('carrier') || text.includes('pricing')) {
    return (candidate: MaFusionModeledTargetPosition) =>
      `${candidate.title} ${candidate.functionName ?? ''}`.toLowerCase().includes('nast');
  }
  if (
    text.includes('shared') ||
    text.includes('corporate') ||
    text.includes('finance') ||
    text.includes('legal') ||
    text.includes('hr') ||
    text.includes('compliance') ||
    text.includes('technology')
  ) {
    return (candidate: MaFusionModeledTargetPosition) => {
      const candidateText = `${candidate.title} ${candidate.functionName ?? ''}`.toLowerCase();
      return (
        candidateText.includes('corporate') ||
        candidateText.includes('finance') ||
        candidateText.includes('technology') ||
        candidateText.includes('compliance')
      );
    };
  }
  return null;
}

const DEAL_CANVAS_MAX_NODES_PER_ROW = 12;
const DEAL_CANVAS_NODE_X_GAP = 280;
const DEAL_CANVAS_NODE_Y_GAP = 180;
const DEAL_CANVAS_LAYER_GAP = 80;
const DEAL_CANVAS_TARGET_X_OFFSET =
  DEAL_CANVAS_MAX_NODES_PER_ROW * DEAL_CANVAS_NODE_X_GAP + 720;
const DEAL_CANVAS_CERTAINTY_CLASSES = new Set<string>(MA_TARGET_PACK_CERTAINTY_CLASSES);
const DEAL_CANVAS_MATCH_METHODS = new Set<string>([
  'exact_function_title',
  'modeled_overlap_zone',
]);

function dealCanvasNodeId(
  orgSide: MaFusionDealCanvasOrgSide,
  positionId: string,
): string {
  return `${orgSide.toLowerCase()}:${positionId}`;
}

function requireDealCanvasOrganization(
  organization: MaFusionDealCanvasOrganization,
  expectedSide: MaFusionDealCanvasOrgSide,
): Map<string, MaFusionModeledTargetPosition> {
  if (organization.orgSide !== expectedSide) {
    throw new Error(
      `two-org deal canvas ${expectedSide} organization must declare orgSide ${expectedSide}`,
    );
  }
  if (!organization.orgStateId.trim()) {
    throw new Error(`two-org deal canvas ${expectedSide} orgStateId is required`);
  }
  if (!organization.name.trim()) {
    throw new Error(`two-org deal canvas ${expectedSide} organization name is required`);
  }
  if (organization.positions.length === 0) {
    throw new Error(
      `two-org deal canvas ${expectedSide} organization must include at least one position`,
    );
  }

  const positionById = new Map<string, MaFusionModeledTargetPosition>();
  for (const position of organization.positions) {
    const positionId = position.positionId.trim();
    if (!positionId) {
      throw new Error(`two-org deal canvas ${expectedSide} positionId is required`);
    }
    if (positionById.has(positionId)) {
      throw new Error(
        `two-org deal canvas ${expectedSide} positionId ${positionId} is duplicated`,
      );
    }
    positionById.set(positionId, position);
  }

  for (const position of organization.positions) {
    const managerId = position.reportsTo?.trim() || null;
    if (managerId && !positionById.has(managerId)) {
      throw new Error(
        `two-org deal canvas ${expectedSide} position ${position.positionId} references unknown manager ${managerId}`,
      );
    }
  }
  for (const person of organization.people) {
    if (!positionById.has(person.positionId)) {
      throw new Error(
        `two-org deal canvas ${expectedSide} person ${person.personId} references unknown position ${person.positionId}`,
      );
    }
  }

  return positionById;
}

function dealCanvasPositionDepth(
  positionId: string,
  positionById: ReadonlyMap<string, MaFusionModeledTargetPosition>,
  depthById: Map<string, number>,
  visiting: Set<string>,
): number {
  const existing = depthById.get(positionId);
  if (existing != null) return existing;
  if (visiting.has(positionId)) {
    throw new Error(`two-org deal canvas hierarchy contains a cycle at ${positionId}`);
  }

  const position = positionById.get(positionId);
  if (!position) {
    throw new Error(`two-org deal canvas cannot lay out unknown position ${positionId}`);
  }
  visiting.add(positionId);
  const managerId = position.reportsTo?.trim() || null;
  const depth = managerId
    ? dealCanvasPositionDepth(managerId, positionById, depthById, visiting) + 1
    : 0;
  visiting.delete(positionId);
  depthById.set(positionId, depth);
  return depth;
}

function layoutDealCanvasOrganization(
  positionById: ReadonlyMap<string, MaFusionModeledTargetPosition>,
  baseX: number,
): Map<string, Readonly<{ x: number; y: number }>> {
  const depthById = new Map<string, number>();
  const positionsByDepth = new Map<number, MaFusionModeledTargetPosition[]>();

  for (const position of positionById.values()) {
    const depth = dealCanvasPositionDepth(
      position.positionId,
      positionById,
      depthById,
      new Set<string>(),
    );
    positionsByDepth.set(depth, [
      ...(positionsByDepth.get(depth) ?? []),
      position,
    ]);
  }

  const layout = new Map<string, Readonly<{ x: number; y: number }>>();
  let layerTop = 0;
  for (const depth of [...positionsByDepth.keys()].sort((left, right) => left - right)) {
    const positions = [...(positionsByDepth.get(depth) ?? [])].sort((left, right) => {
      const titleOrder = left.title.localeCompare(right.title);
      return titleOrder !== 0
        ? titleOrder
        : left.positionId.localeCompare(right.positionId);
    });
    positions.forEach((position, index) => {
      layout.set(position.positionId, {
        x: baseX + (index % DEAL_CANVAS_MAX_NODES_PER_ROW) * DEAL_CANVAS_NODE_X_GAP,
        y: layerTop + Math.floor(index / DEAL_CANVAS_MAX_NODES_PER_ROW) * DEAL_CANVAS_NODE_Y_GAP,
      });
    });
    const rows = Math.max(
      1,
      Math.ceil(positions.length / DEAL_CANVAS_MAX_NODES_PER_ROW),
    );
    layerTop += rows * DEAL_CANVAS_NODE_Y_GAP + DEAL_CANVAS_LAYER_GAP;
  }
  return layout;
}

function positionCertaintyClass(
  position: MaFusionModeledTargetPosition,
): MaTargetPackCertaintyClass | null {
  return (
    position.maEvidence?.headcount?.certaintyClass ??
    position.maEvidence?.title?.certaintyClass ??
    null
  );
}

function projectDealCanvasOrganization(
  organization: MaFusionDealCanvasOrganization,
  positionById: ReadonlyMap<string, MaFusionModeledTargetPosition>,
  baseX: number,
): Readonly<{
  nodes: readonly MaFusionDealCanvasNode[];
  edges: readonly MaFusionDealCanvasEdge[];
}> {
  const layout = layoutDealCanvasOrganization(positionById, baseX);
  const occupantByPositionId = new Map(
    organization.people.map((person) => [person.positionId, person] as const),
  );
  const sortedPositions = [...positionById.values()].sort((left, right) =>
    left.positionId.localeCompare(right.positionId),
  );

  const nodes = sortedPositions.map((position): MaFusionDealCanvasNode => {
    const occupant = occupantByPositionId.get(position.positionId) ?? null;
    const isRoot = !position.reportsTo?.trim();
    return {
      id: dealCanvasNodeId(organization.orgSide, position.positionId),
      type: 'orgNodeCard',
      position: layout.get(position.positionId) ?? { x: baseX, y: 0 },
      data: {
        label: isRoot
          ? `${organization.name} — ${position.title}`
          : position.title,
        title: isRoot
          ? `${organization.name} — ${position.title}`
          : position.title,
        positionId: position.positionId,
        orgStateId: organization.orgStateId,
        occupantName: occupant?.name ?? null,
        occupantId: occupant?.personId ?? null,
        isRoot,
        attrs: {
          orgSide: organization.orgSide,
          organizationName: organization.name,
          reportsTo: position.reportsTo?.trim() || null,
          functionName: position.functionName ?? null,
          level: position.level ?? null,
          location: position.location ?? null,
          modeledHeadcountRange: position.modeledHeadcountRange ?? null,
          certaintyClass: positionCertaintyClass(position),
        },
      },
    };
  });

  const edges = sortedPositions.flatMap((position): MaFusionDealCanvasEdge[] => {
    const managerId = position.reportsTo?.trim() || null;
    if (!managerId) return [];
    return [
      {
        id: `manages:${organization.orgSide.toLowerCase()}:${managerId}:${position.positionId}`,
        source: dealCanvasNodeId(organization.orgSide, managerId),
        target: dealCanvasNodeId(organization.orgSide, position.positionId),
        type: 'smoothstep',
        data: {
          kind: 'MANAGES',
          orgSide: organization.orgSide,
        },
      },
    ];
  });

  return { nodes, edges };
}

export function projectMaFusionTwoOrgDealCanvas(
  model: MaFusionTwoOrgDealCanvas,
): MaFusionTwoOrgDealCanvasProjection {
  if (model.schemaVersion !== MA_FUSION_TWO_ORG_DEAL_CANVAS_SCHEMA_VERSION) {
    throw new Error(
      `two-org deal canvas schemaVersion must be ${MA_FUSION_TWO_ORG_DEAL_CANVAS_SCHEMA_VERSION}`,
    );
  }

  const acquirer = model.organizations.acquirer;
  const target = model.organizations.target;
  const primaryPositionById = requireDealCanvasOrganization(acquirer, 'PRIMARY');
  const targetPositionById = requireDealCanvasOrganization(target, 'TARGET');
  if (acquirer.orgStateId === target.orgStateId) {
    throw new Error('two-org deal canvas requires distinct acquirer and target orgStateIds');
  }

  const familyIds = new Set<string>();
  for (const family of model.sharedMemberFamilies) {
    if (!family.familyId.trim()) {
      throw new Error('two-org deal canvas overlap familyId is required');
    }
    if (familyIds.has(family.familyId)) {
      throw new Error(
        `two-org deal canvas overlap familyId ${family.familyId} is duplicated`,
      );
    }
    familyIds.add(family.familyId);
    if (!DEAL_CANVAS_CERTAINTY_CLASSES.has(family.certaintyClass)) {
      throw new Error(
        'two-org deal canvas overlap family certaintyClass must be public_filing, ir_material, or extrapolated_assumption',
      );
    }
    if (!DEAL_CANVAS_MATCH_METHODS.has(family.matchMethod)) {
      throw new Error(
        'two-org deal canvas overlap family matchMethod must be exact_function_title or modeled_overlap_zone',
      );
    }

    const sides = new Set(family.members.map((member) => member.orgSide));
    if (!sides.has('PRIMARY') || !sides.has('TARGET')) {
      throw new Error(
        `two-org deal canvas overlap family ${family.familyId} must include at least one PRIMARY and one TARGET member`,
      );
    }
    const memberKeys = new Set<string>();
    for (const member of family.members) {
      if (member.orgSide !== 'PRIMARY' && member.orgSide !== 'TARGET') {
        throw new Error(
          'two-org deal canvas overlap family member orgSide must be PRIMARY or TARGET',
        );
      }
      const memberKey = `${member.orgSide}:${member.positionId}`;
      if (memberKeys.has(memberKey)) {
        throw new Error(
          `two-org deal canvas overlap family ${family.familyId} duplicates member ${memberKey}`,
        );
      }
      memberKeys.add(memberKey);
      const positionById =
        member.orgSide === 'PRIMARY' ? primaryPositionById : targetPositionById;
      if (!positionById.has(member.positionId)) {
        throw new Error(
          `two-org deal canvas overlap family ${family.familyId} references unknown ${member.orgSide} position ${member.positionId}`,
        );
      }
    }
  }

  const primaryProjection = projectDealCanvasOrganization(
    acquirer,
    primaryPositionById,
    0,
  );
  const targetProjection = projectDealCanvasOrganization(
    target,
    targetPositionById,
    DEAL_CANVAS_TARGET_X_OFFSET,
  );
  const overlapEdges = [...model.sharedMemberFamilies]
    .sort((left, right) => left.familyId.localeCompare(right.familyId))
    .flatMap((family) => {
      const primaryMembers = family.members
        .filter((member) => member.orgSide === 'PRIMARY')
        .sort((left, right) => left.positionId.localeCompare(right.positionId));
      const targetMembers = family.members
        .filter((member) => member.orgSide === 'TARGET')
        .sort((left, right) => left.positionId.localeCompare(right.positionId));
      const memberPositionIds = [...family.members]
        .map((member) => `${member.orgSide}:${member.positionId}`)
        .sort();

      return primaryMembers.flatMap((primaryMember) =>
        targetMembers.map(
          (targetMember): MaFusionDealCanvasEdge => ({
            id: `overlap:${family.familyId}:${primaryMember.positionId}:${targetMember.positionId}`,
            source: dealCanvasNodeId('PRIMARY', primaryMember.positionId),
            target: dealCanvasNodeId('TARGET', targetMember.positionId),
            type: 'smoothstep',
            label: family.label,
            animated: true,
            style: { strokeDasharray: '6 4', strokeWidth: 2 },
            data: {
              kind: 'OVERLAP',
              familyId: family.familyId,
              certaintyClass: family.certaintyClass,
              matchMethod: family.matchMethod,
              memberPositionIds,
            },
          }),
        ),
      );
    });

  return {
    schemaVersion: MA_FUSION_TWO_ORG_DEAL_CANVAS_PROJECTION_SCHEMA_VERSION,
    nodes: [...primaryProjection.nodes, ...targetProjection.nodes],
    edges: [
      ...primaryProjection.edges,
      ...targetProjection.edges,
      ...overlapEdges,
    ],
  };
}

const SCENARIO_ROSTER_FIRST_NAMES = [
  'Amelia', 'Arjun', 'Bianca', 'Caleb', 'Daniela', 'Elliot', 'Farah', 'Gabriel',
  'Hannah', 'Isaac', 'Jasmine', 'Kai', 'Leila', 'Marcus', 'Nadia', 'Owen',
  'Priya', 'Quentin', 'Rosa', 'Samuel', 'Talia', 'Uri', 'Valeria', 'Wesley',
  'Ximena', 'Yara', 'Zachary', 'Aisha', 'Brandon', 'Camille', 'Devon', 'Elena',
  'Felix', 'Grace', 'Harper', 'Imran', 'Joanna', 'Kieran', 'Lena', 'Mateo',
  'Noor', 'Olivia', 'Parker', 'Ravi', 'Sofia', 'Theo', 'Una', 'Victor',
] as const;

const SCENARIO_ROSTER_LAST_NAMES = [
  'Adler', 'Bennett', 'Caldwell', 'Dawson', 'Ellis', 'Fischer', 'Garcia', 'Hughes',
  'Ibrahim', 'Jensen', 'Kim', 'Lawson', 'Moretti', 'Nguyen', 'Okafor', 'Patel',
  'Quinn', 'Ramirez', 'Sato', 'Turner', 'Usman', 'Vega', 'Walker', 'Xu', 'Young',
  'Zhou', 'Alvarez', 'Brooks', 'Chandra', 'Delgado', 'Evans', 'Fletcher', 'Gomez',
  'Hayes', 'Ito', 'Johnson', 'Khan', 'Lopez', 'Martinez', 'Nair', 'Owens', 'Petrov',
  'Reed', 'Singh', 'Taylor', 'Vasquez', 'Wilson', 'Yilmaz', 'Zhang', 'Costa',
] as const;

function scenarioRosterName(ordinal: number): string {
  const first = SCENARIO_ROSTER_FIRST_NAMES[ordinal % SCENARIO_ROSTER_FIRST_NAMES.length];
  const last =
    SCENARIO_ROSTER_LAST_NAMES[
      Math.floor(ordinal / SCENARIO_ROSTER_FIRST_NAMES.length) % SCENARIO_ROSTER_LAST_NAMES.length
    ];
  return `${first} ${last}`;
}

function scenarioRosterEvidence(): MaFusionEvidenceRef {
  return {
    certaintyClass: 'extrapolated_assumption',
    sourceRefs: ['helix_scenario_model'],
    notes: 'Detailed deterministic scenario organization generated from the Phoenix integration roster specification.',
  };
}

function buildScenarioRoster(
  pack: MaTargetPack,
  roster: MaTargetPackScenarioRoster,
): Pick<MaFusionModeledTargetOrgState, 'positions' | 'people'> {
  const positions: MaFusionModeledTargetPosition[] = [];
  const people: OrgPerson[] = [];
  const rootPositionId = `ma_target_${pack.packId}_position_0001`;
  const rootOffice = roster.offices[0];
  const rootPersonId = `ma_target_${pack.packId}_person_0001`;
  const rootCost = 320000;

  positions.push({
    positionId: rootPositionId,
    title: roster.rootLeader.title,
    reportsTo: null,
    isManager: true,
    personId: rootPersonId,
    baseCost: rootCost,
    roleType: 'executive',
    functionName: 'Executive and Leadership',
    level: 'C_SUITE',
    location: rootOffice.name,
    storeId: rootOffice.id,
    modeledHeadcountRange: { low: 1, high: 1 },
    maEvidence: { scenarioRoster: scenarioRosterEvidence() },
  });
  people.push({
    personId: rootPersonId,
    name: roster.rootLeader.name,
    positionId: rootPositionId,
    employmentType: 'scenario_employee',
    fte: 1,
    cost: rootCost,
  });

  let generatedCount = 1;
  roster.functions.forEach((rosterFunction, functionIndex) => {
    const isExecutiveFunction = rosterFunction.functionName === 'Executive and Leadership';
    const peopleToGenerate = rosterFunction.headcount - (isExecutiveFunction ? 1 : 0);
    let functionLeaderPositionId = rootPositionId;
    let managerPositionId = rootPositionId;
    const managerTitles = rosterFunction.roleTitles.slice(0, Math.min(3, rosterFunction.roleTitles.length));
    const individualTitles = rosterFunction.roleTitles.slice(managerTitles.length);
    const individualContributorTitles = individualTitles.length
      ? individualTitles
      : rosterFunction.roleTitles;

    for (let offset = 0; offset < peopleToGenerate; offset += 1) {
      generatedCount += 1;
      const sequence = String(generatedCount).padStart(4, '0');
      const positionId = `ma_target_${pack.packId}_position_${sequence}`;
      const personId = `ma_target_${pack.packId}_person_${sequence}`;
      const office = roster.offices[(generatedCount - 1) % roster.offices.length];
      const isFunctionLeader = offset === 0;
      const isManager = isFunctionLeader || offset <= 4 || offset % 15 === 0;
      const title = isFunctionLeader
        ? rosterFunction.executiveTitle
        : isManager
          ? managerTitles[(offset - 1) % managerTitles.length]
          : individualContributorTitles[(offset - 1) % individualContributorTitles.length];
      const cost = isFunctionLeader
        ? 260000 + functionIndex * 6000
        : isManager
          ? 145000 + functionIndex * 3500 + (offset % 7) * 1000
          : 72000 + functionIndex * 2500 + (offset % 12) * 1250;
      const reportsTo = isFunctionLeader ? rootPositionId : isManager ? functionLeaderPositionId : managerPositionId;

      positions.push({
        positionId,
        title,
        reportsTo,
        isManager,
        personId,
        baseCost: cost,
        roleType: isManager ? 'manager' : 'individual_contributor',
        functionName: rosterFunction.functionName,
        level: isFunctionLeader ? 'C_SUITE' : isManager ? 'MANAGER' : 'INDIVIDUAL_CONTRIBUTOR',
        location: office.name,
        storeId: office.id,
        modeledHeadcountRange: { low: 1, high: 1 },
        maEvidence: { scenarioRoster: scenarioRosterEvidence() },
      });
      people.push({
        personId,
        name: scenarioRosterName(generatedCount),
        positionId,
        employmentType: 'scenario_employee',
        fte: 1,
        cost,
      });

      if (isFunctionLeader) functionLeaderPositionId = positionId;
      if (isManager) managerPositionId = positionId;
    }
  });

  if (generatedCount !== roster.totalHeadcount) {
    throw new Error(
      `scenario roster generated ${generatedCount} people but declares ${roster.totalHeadcount}`,
    );
  }
  return { positions, people };
}

export function buildMaFusionModeledTarget(
  pack: MaTargetPack,
  options: BuildModeledTargetOptions
): MaFusionModeledTarget {
  const evidenceByPath: Record<string, MaFusionEvidenceRef> = {};

  addEvidence(evidenceByPath, 'identity.legalName', pack.identity.legalName);
  addEvidence(evidenceByPath, 'identity.ticker', pack.identity.ticker);
  addEvidence(evidenceByPath, 'identity.industry', pack.identity.industry);
  addEvidence(evidenceByPath, 'identity.headquarters', pack.identity.headquarters);
  addEvidence(evidenceByPath, 'identity.acquisitionResonance', pack.identity.acquisitionResonance);
  addEvidence(evidenceByPath, 'financials.fiscalPeriod', pack.financials.fiscalPeriod);
  addEvidence(evidenceByPath, 'financials.revenueUsd', pack.financials.revenueUsd);
  addEvidence(evidenceByPath, 'financials.operatingIncomeUsd', pack.financials.operatingIncomeUsd);
  addEvidence(evidenceByPath, 'financials.sgaUsd', pack.financials.sgaUsd);
  addEvidence(evidenceByPath, 'workforce.totalHeadcount', pack.workforce.totalHeadcount);

  let positions: MaFusionModeledTargetPosition[] = [];
  let people: OrgPerson[] = [];

  pack.leadership.topTwoLayers.forEach((leader, idx) => {
    addEvidence(evidenceByPath, `leadership.topTwoLayers[${idx}].name`, leader.name);
    addEvidence(evidenceByPath, `leadership.topTwoLayers[${idx}].title`, leader.title);
    addEvidence(evidenceByPath, `leadership.topTwoLayers[${idx}].layer`, leader.layer);
  });
  pack.workforce.byFunction.forEach((row, idx) => {
    addEvidence(evidenceByPath, `workforce.byFunction[${idx}].functionName`, row.functionName);
    addEvidence(evidenceByPath, `workforce.byFunction[${idx}].headcount`, row.headcount);
  });

  if (pack.scenarioRoster) {
    ({ positions, people } = buildScenarioRoster(pack, pack.scenarioRoster));
  } else {
    const rootPositionId = `ma_target_${pack.packId}_ceo`;
    pack.leadership.topTwoLayers.forEach((leader, idx) => {
      const positionId = idx === 0 ? rootPositionId : `ma_target_${pack.packId}_leader_${idx}`;
      const title = leader.title.value;
      const personId = `ma_target_${pack.packId}_person_${idx}`;

      positions.push({
        positionId,
        title,
        reportsTo: leader.layer.value === 'layer_1' ? null : rootPositionId,
        isManager: true,
        personId,
        functionName: inferFunctionFromTitle(title),
        level: leader.layer.value,
        location: pack.identity.headquarters.value,
        maEvidence: {
          name: evidenceRef(leader.name),
          title: evidenceRef(leader.title),
          layer: evidenceRef(leader.layer),
        },
      });
      people.push({
        personId,
        name: leader.name.value,
        positionId,
        employmentType: 'public_leadership',
        fte: null,
        cost: null,
      });
    });

    pack.workforce.byFunction.forEach((row, idx) => {
      const functionName = row.functionName.value;
      const lower = functionName.toLowerCase();
      const reportsTo =
        positions.find((position) => {
          const fn = String(position.functionName ?? '').toLowerCase();
          return (
            (lower.includes('forwarding') && fn.includes('forwarding')) ||
            (lower.includes('brokerage') && fn.includes('brokerage')) ||
            (lower.includes('shared') && fn.includes('corporate')) ||
            (lower.includes('finance') && fn.includes('corporate'))
          );
        })?.positionId ?? rootPositionId;

      positions.push({
        positionId: `ma_target_${pack.packId}_pool_${idx}`,
        title: functionName,
        reportsTo,
        isManager: false,
        personId: null,
        functionName,
        level: 'modeled_pool',
        location: 'modeled_envelope',
        modeledHeadcountRange: row.headcount.value,
        maEvidence: {
          functionName: evidenceRef(row.functionName),
          headcount: evidenceRef(row.headcount),
        },
      });
    });
  }

  pack.workforce.byGeography.forEach((row, idx) => {
    addEvidence(evidenceByPath, `workforce.byGeography[${idx}].geography`, row.geography);
    addEvidence(evidenceByPath, `workforce.byGeography[${idx}].headcount`, row.headcount);
  });
  addEvidence(
    evidenceByPath,
    'workforce.compCostAggregates.annualCompCostEnvelopeUsd',
    pack.workforce.compCostAggregates.annualCompCostEnvelopeUsd
  );
  addEvidence(
    evidenceByPath,
    'workforce.compCostAggregates.benefitsLoadPct',
    pack.workforce.compCostAggregates.benefitsLoadPct
  );
  addEvidence(evidenceByPath, 'strategy.statedStrategy', pack.strategy.statedStrategy);
  pack.strategy.integrationHooks.forEach((field, idx) =>
    addEvidence(evidenceByPath, `strategy.integrationHooks[${idx}]`, field)
  );
  addEvidence(evidenceByPath, 'dealThesis.whyChrwWouldBuy', pack.dealThesis.whyChrwWouldBuy);
  addEvidence(
    evidenceByPath,
    'dealThesis.integrateAndCutCostThesis',
    pack.dealThesis.integrateAndCutCostThesis
  );
  pack.dealThesis.expectedOverlapZones.forEach((field, idx) =>
    addEvidence(evidenceByPath, `dealThesis.expectedOverlapZones[${idx}]`, field)
  );

  const dealThesis: MaTargetPackField<string> = {
    value: options.dealThesis,
    certaintyClass: 'extrapolated_assumption',
    sourceRefs: ['helix_certainty_policy'],
    notes: 'Scenario input supplied by Helix M&A demo operator; not a target-disclosed fact.',
  };
  addEvidence(evidenceByPath, 'scenario.dealThesis', dealThesis);

  const orgState: MaFusionModeledTargetOrgState = {
    orgStateId: `ma_target_${pack.packId}_modeled_v1`,
    tenantId: options.tenantId,
    name: pack.scenarioRoster?.organizationName ?? `Modeled target: ${pack.identity.legalName.value}`,
    positions,
    people,
  };

  return {
    schemaVersion: 'ma_fusion_modeled_target_v1',
    packId: pack.packId,
    targetClass: pack.targetClass,
    orgState,
    dealThesis,
    evidenceByPath,
    certaintySummary: summarizeCertainty(evidenceByPath),
    financials: pack.financials,
    workforce: pack.workforce,
    strategy: pack.strategy,
    targetDealThesis: pack.dealThesis,
    dataGaps: pack.dataGaps,
    sources: pack.sources,
  };
}

function parseCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let quoted = false;

  for (let idx = 0; idx < line.length; idx += 1) {
    const char = line[idx];
    if (char === '"') {
      if (quoted && line[idx + 1] === '"') {
        current += '"';
        idx += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (char === ',' && !quoted) {
      values.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  values.push(current);
  return values;
}

function buildAcquirerOrgState(
  input: BuildDemoDryRunInput['acquirer']
): MaFusionModeledTargetOrgState {
  const lines = input.orgStructureCsv
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const headers = parseCsvLine(lines[0] ?? '').map((header) => header.trim());
  const rows = lines.slice(1).map((line) => {
    const values = parseCsvLine(line);
    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] ?? '';
    });
    return row;
  });

  const positions: MaFusionModeledTargetPosition[] = [];
  const people: OrgPerson[] = [];

  for (const row of rows) {
    const positionId = row.position_id?.trim();
    const title = row.title?.trim();
    if (positionId && title) {
      const functionName = row.unit_id?.trim();
      const location = row.location?.trim();
      const level = row.level?.trim();
      positions.push({
        positionId,
        title,
        reportsTo: row.manager_position_id?.trim() || null,
        isManager: row.is_manager?.toLowerCase() === 'true',
        personId: row.person_id?.trim() || null,
        ...(functionName ? { functionName } : {}),
        ...(location ? { location } : {}),
        ...(level ? { level } : {}),
      });
    }

    const personId = row.person_id?.trim();
    const personPositionId = row.position_id?.trim();
    const name = row.person_name?.trim();
    if (personId && personPositionId && name) {
      people.push({
        personId,
        positionId: personPositionId,
        name,
        employmentType: 'chrw_uat_modeled',
        fte: null,
        cost: null,
      });
    }
  }

  return {
    orgStateId: input.orgStateId,
    tenantId: input.tenantId,
    name: input.name,
    positions,
    people,
  };
}

function buildCombinedSnapshot(
  primary: MaFusionModeledTargetOrgState,
  target: MaFusionModeledTargetOrgState
): MaFusionDemoDryRun['combinedSnapshot'] {
  return {
    name: `Fusion dry-run combined: ${primary.name} + ${target.name}`,
    positions: [
      {
        positionId: 'fusion_dry_run_root',
        title: 'Combined Org',
        reportsTo: null,
        isManager: true,
        functionName: 'Combined',
        level: 'root',
      },
      ...primary.positions.map((position) => ({
        ...position,
        positionId: `p_${position.positionId}`,
        reportsTo: position.reportsTo ? `p_${position.reportsTo}` : 'fusion_dry_run_root',
        origin: 'PRIMARY',
      })),
      ...target.positions.map((position) => ({
        ...position,
        positionId: `t_${position.positionId}`,
        reportsTo: position.reportsTo ? `t_${position.reportsTo}` : 'fusion_dry_run_root',
        origin: 'TARGET',
      })),
    ],
    people: [
      ...primary.people.map((person) => ({
        ...person,
        personId: `p_${person.personId}`,
        positionId: `p_${person.positionId}`,
      })),
      ...target.people.map((person) => ({
        ...person,
        personId: `t_${person.personId}`,
        positionId: `t_${person.positionId}`,
      })),
    ],
  };
}

function buildCostByPosition(rows: readonly MaFusionDryRunOrgCostRow[]) {
  return new Map(rows.map((row) => [row.positionId, row]));
}

function buildWorkforceRows(
  orgState: MaFusionModeledTargetOrgState,
  costs: Map<string, MaFusionDryRunOrgCostRow>,
  org: 'A' | 'B'
) {
  const peopleByPosition = new Map(
    orgState.people.map((person) => [person.positionId, person.name])
  );
  return orgState.positions.map((position) => {
    const cost = costs.get(position.positionId) ?? null;
    return {
      empId: position.personId ?? position.positionId,
      name: peopleByPosition.get(position.positionId) ?? 'Unknown',
      title: position.title,
      function: normalizeFunctionName(position.functionName),
      level: position.level ?? '',
      org,
      manager: position.reportsTo ?? '',
      location: position.location ?? '',
      salary: cost?.fullyLoadedAnnualCost ?? position.baseCost ?? null,
      currency: cost?.currency ?? null,
      decision: 'PENDING',
    };
  });
}

function buildOverlapSequence(
  acquirer: MaFusionModeledTargetOrgState,
  acquirerCosts: readonly MaFusionDryRunOrgCostRow[],
  target: MaFusionModeledTarget
): MaFusionDemoDryRun['overlapSequence'] {
  const targetCostByPosition = new Map<string, MaFusionDryRunOrgCostRow>();
  const acquirerCostByPosition = buildCostByPosition(acquirerCosts);
  const workforceRows = [
    ...buildWorkforceRows(acquirer, acquirerCostByPosition, 'A'),
    ...buildWorkforceRows(target.orgState, targetCostByPosition, 'B'),
  ];
  const functions = Array.from(
    new Set(workforceRows.map((row) => row.function).filter(Boolean))
  ).sort();
  const locations = Array.from(
    new Set(workforceRows.map((row) => row.location).filter(Boolean))
  ).sort();

  const acquirerByGroup = new Map<string, MaFusionModeledTargetPosition[]>();
  for (const position of acquirer.positions) {
    const key = `${normalizeFunctionName(position.functionName)}||${normalizeTitleKey(position.title)}`;
    const existing = acquirerByGroup.get(key) ?? [];
    existing.push(position);
    acquirerByGroup.set(key, existing);
  }

  const crosswalkRows: Record<string, unknown>[] = [];
  const redundancyRows: Record<string, unknown>[] = [];
  const sharedMemberFamilies: MaFusionDealCanvasSharedMemberFamily[] = [];
  for (const targetPosition of target.orgState.positions) {
    const key = `${normalizeFunctionName(targetPosition.functionName)}||${normalizeTitleKey(targetPosition.title)}`;
    const exactMatches = acquirerByGroup.get(key) ?? [];
    const overlapMatcher = targetToAcquirerOverlapMatcher(targetPosition);
    const modeledMatches = overlapMatcher ? acquirer.positions.filter(overlapMatcher) : [];
    const matches = exactMatches.length ? exactMatches : modeledMatches;
    if (!matches.length) continue;
    const matchMethod = exactMatches.length
      ? 'exact_function_title' as const
      : 'modeled_overlap_zone' as const;
    const certaintyClass = exactMatches.length
      ? targetPosition.maEvidence?.title?.certaintyClass ?? 'extrapolated_assumption'
      : 'extrapolated_assumption';
    const overlapFamilyId = `ma_overlap:${targetPosition.positionId}`;
    const primaryPositionIds = matches
      .map((position) => position.positionId)
      .sort((left, right) => left.localeCompare(right));
    crosswalkRows.push({
      targetPositionId: targetPosition.positionId,
      targetTitle: targetPosition.title,
      functionName: targetPosition.functionName ?? null,
      primaryCandidateCount: matches.length,
      primaryPositionIds,
      overlapFamilyId,
      confidencePct: exactMatches.length ? 100 : 65,
      matchMethod,
      certaintyClass,
    });
    sharedMemberFamilies.push({
      familyId: overlapFamilyId,
      label: targetPosition.functionName ?? targetPosition.title,
      certaintyClass,
      matchMethod,
      members: [
        ...primaryPositionIds.map((positionId) => ({
          orgSide: 'PRIMARY' as const,
          positionId,
        })),
        {
          orgSide: 'TARGET' as const,
          positionId: targetPosition.positionId,
        },
      ],
    });
    redundancyRows.push({
      titleLabel: targetPosition.title,
      functionName: targetPosition.functionName ?? null,
      primaryCount: matches.length,
      targetCount: 1,
      overlapCount: 1,
      severity: matches.length >= 3 ? 'MEDIUM' : 'LOW',
      certaintyClass:
        targetPosition.maEvidence?.headcount?.certaintyClass ??
        targetPosition.maEvidence?.title?.certaintyClass ??
        'extrapolated_assumption',
    });
  }

  const leadershipRows = target.orgState.positions
    .filter((position) => position.level === 'layer_1' || position.level === 'layer_2')
    .map((targetLeader) => {
      const normalizedTargetTitle = normalizeTitleKey(targetLeader.title);
      const primary =
        acquirer.positions.find(
          (position) => normalizeTitleKey(position.title) === normalizedTargetTitle
        ) ?? null;
      return {
        roleLabel: targetLeader.title,
        primaryPositionId: primary?.positionId ?? null,
        targetPositionId: targetLeader.positionId,
        decision: primary ? 'OVERLAP_REVIEW_REQUIRED' : 'TARGET_ONLY_REVIEW_REQUIRED',
        certaintyClass: targetLeader.maEvidence?.title?.certaintyClass ?? 'ir_material',
      };
    });

  return {
    workforceGrid: {
      totalRows: workforceRows.length,
      functions,
      locations,
      rows: workforceRows.slice(0, 12),
    },
    crosswalk: { rows: crosswalkRows },
    leadershipSlate: { rows: leadershipRows },
    redundancyCandidates: { rows: redundancyRows },
    sharedMemberFamilies,
  };
}

export function buildMaFusionDemoDryRun(input: BuildDemoDryRunInput): MaFusionDemoDryRun {
  const target = buildMaFusionModeledTarget(input.targetPack, {
    tenantId: input.acquirer.tenantId,
    dealThesis: input.dealThesis,
  });
  const acquirerOrgState = buildAcquirerOrgState(input.acquirer);
  const combinedSnapshot = buildCombinedSnapshot(acquirerOrgState, target.orgState);
  const overlapSequence = buildOverlapSequence(acquirerOrgState, input.acquirer.orgCostRows, target);
  const twoOrgDealCanvasModel: MaFusionTwoOrgDealCanvas = {
    schemaVersion: MA_FUSION_TWO_ORG_DEAL_CANVAS_SCHEMA_VERSION,
    organizations: {
      acquirer: {
        orgSide: 'PRIMARY',
        orgStateId: acquirerOrgState.orgStateId,
        name: acquirerOrgState.name,
        positions: acquirerOrgState.positions,
        people: acquirerOrgState.people,
      },
      target: {
        orgSide: 'TARGET',
        orgStateId: target.orgState.orgStateId,
        name: target.orgState.name,
        positions: target.orgState.positions,
        people: target.orgState.people,
      },
    },
    sharedMemberFamilies: overlapSequence.sharedMemberFamilies,
  };

  return {
    mode: 'dry_run_no_db_writes',
    scenario: {
      name: `CHRW x ${input.targetPack.identity.legalName.value} - ${input.dealThesis}`,
      primaryOrgStateId: acquirerOrgState.orgStateId,
      targetOrgStateId: target.orgState.orgStateId,
      dealThesis: input.dealThesis,
      assumptionsJson: {
        source: 'ma_target_pack',
        packId: input.targetPack.packId,
        certaintySummary: target.certaintySummary,
        dataGaps: input.targetPack.dataGaps,
      },
    },
    acquirer: {
      orgState: acquirerOrgState,
      costRows: input.acquirer.orgCostRows,
    },
    target,
    combinedSnapshot,
    overlapSequence,
    twoOrgDealCanvas: {
      model: twoOrgDealCanvasModel,
      projection: projectMaFusionTwoOrgDealCanvas(twoOrgDealCanvasModel),
    },
    m3GapList: [
      'Live Fusion scenario creation and route rendering require persistence: persisted Fusion workspaces still require Prisma OrgState IDs and scenario versions; this dry-run proves the modeled target before any DB write.',
      'Workforce grid can render CHRW rows from governed UAT org/cost data, but target rows carry modeled headcount envelopes rather than person-level HRIS rows until diligence or Day-1 census arrives.',
      'Role crosswalk and redundancy candidates use current exact-title/function matching; M3 needs seat-archetype matching for forwarding, brokerage, carrier procurement, and shared services.',
      'Leadership slate reads public top-two-layer target leaders, but CHRW-side leadership matching depends on normalized title coverage and should expose certainty labels in the UI.',
      'Synergy commitments are not yet emitted as drift-watched realization commitments; next slice adds SynergyLeverCommitment, ma_fusion_scenario_activation activation receipts, and one CHRW lever projection into the existing Decision Ledger realization commitment and drift-waterfall path.',
    ],
  };
}
